import { useCallback } from "react";

import {
	useAddUserFavoriteGameMutation,
	useGetUserFavoriteGamesQuery,
	useRemoveUserFavoriteGameMutation,
} from "@api";
import { getUser } from "@utils";

const resolveGameId = (game) => game?.id ?? null;

export const useBettoFavoriteGames = ({ size = 200, offset = 0, skip = false } = {}) => {
	const user = getUser();
	const shouldSkip = skip || !user || window.TENANT !== "betto";

	const { data, isLoading, isFetching } = useGetUserFavoriteGamesQuery(
		{ size, offset },
		{ skip: shouldSkip }
	);

	const [addFavoriteMutation, { isLoading: isAdding }] = useAddUserFavoriteGameMutation();
	const [removeFavoriteMutation, { isLoading: isRemoving }] = useRemoveUserFavoriteGameMutation();

	const favoriteGames = data?.games ?? [];

	const isGameFavorite = useCallback(
		(gameId) => {
			if (!gameId) return false;

			const normalizedId = String(gameId);
			return favoriteGames.some((game) => String(resolveGameId(game)) === normalizedId);
		},
		[favoriteGames]
	);

	const addFavorite = useCallback(
		(gameId) => {
			if (!gameId || isAdding) return Promise.resolve(null);

			return addFavoriteMutation(String(gameId)).unwrap();
		},
		[addFavoriteMutation, isAdding]
	);

	const removeFavorite = useCallback(
		(gameId) => {
			if (!gameId || isRemoving) return Promise.resolve(null);

			return removeFavoriteMutation(String(gameId)).unwrap();
		},
		[isRemoving, removeFavoriteMutation]
	);

	return {
		favoriteGames,
		isLoading: isLoading || isFetching,
		isAdding,
		isRemoving,
		isGameFavorite,
		addFavorite,
		removeFavorite,
	};
};

export default useBettoFavoriteGames;
